import { createContext, useCallback, useContext } from 'react';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import { buildUrl } from '../utils/buildQuery';
import { normalizeDate } from '../utils/formatDate';

const LlmLogApiContext = createContext(null);

export function LlmLogApiProvider({ children }) {
  const { get } = useApiService(CONFIG.SERVER_ORGANIZATION);

  const getLlmLogs = useCallback(
    async (skip, limit, start_date, end_date, model_name = null, usage_type = null) => {
      try {
        const url = buildUrl('/v1/audit/llm', {
          skip,
          limit,
          start_date: normalizeDate(start_date),
          end_date: normalizeDate(end_date),
          model_name,
          usage_type,
        });

        const response = await get(url);

        return response.data;
      } catch (error) {
        console.error('Error fetching llm logs:', error);
        throw error;
      }
    },
    [get],
  );

  const getLlmLogById = useCallback(
    async (llmLogId) => {
      try {
        const response = await get(`/v1/audit/llm/${llmLogId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching llm log by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const getLlmLogModels = useCallback(
    async (start_date, end_date) => {
      try {
        const url = buildUrl('/v1/audit/llm/models', {
          start_date: normalizeDate(start_date),
          end_date: normalizeDate(end_date),
        });

        const response = await get(url);
        return response.data;
      } catch (error) {
        console.error('Error fetching llm log models:', error);
        throw error;
      }
    },
    [get],
  );

  const getLlmLogUsageTypes = useCallback(
    async (start_date, end_date) => {
      try {
        const url = buildUrl('/v1/audit/llm/usage-types', {
          start_date: normalizeDate(start_date),
          end_date: normalizeDate(end_date),
        });

        const response = await get(url);
        return response.data;
      } catch (error) {
        console.error('Error fetching llm log usage types:', error);
        throw error;
      }
    },
    [get],
  );

  const getTokenAnalyticsByLLM = useCallback(
    async (start_date, end_date, skip = 0, limit = 100) => {
      try {
        const url = buildUrl('/v1/audit/llm/channel/tokens/llm', {
          start_date: normalizeDate(start_date),
          end_date: normalizeDate(end_date),
          skip,
          limit,
        });

        const response = await get(url);
        return response.data;
      } catch (error) {
        console.error('Error fetching token analytics by LLM:', error);
        throw error;
      }
    },
    [get],
  );

  const getTokenAnalytics = useCallback(
    async (start_date, end_date) => {
      try {
        const url = buildUrl('/v1/audit/llm/channel/tokens', {
          start_date: normalizeDate(start_date),
          end_date: normalizeDate(end_date),
        });

        const response = await get(url);
        return response.data;
      } catch (error) {
        console.error('Error fetching token analytics:', error);
        throw error;
      }
    },
    [get],
  );

  return (
    <LlmLogApiContext.Provider
      value={{
        getLlmLogs,
        getLlmLogById,
        getLlmLogModels,
        getLlmLogUsageTypes,
        getTokenAnalyticsByLLM,
        getTokenAnalytics,
      }}
    >
      {children}
    </LlmLogApiContext.Provider>
  );
}

export function useLlmLogApi() {
  const context = useContext(LlmLogApiContext);
  if (!context) {
    throw new Error('useLlmLogApi must be used within a LlmLogApiProvider');
  }
  return context;
}
